import { DEFAULT_LOCALE, type Locale, RELEASED_LOCALES, SUPPORTED_LOCALES } from './config';

type ResolveLocaleInput = {
  /** What this device last ran in, from the app's `LocaleStore`. */
  stored?: string | null;
  /** The device's preferred languages, most preferred first, as BCP 47 tags (`it-IT`, `en-GB`). */
  deviceLocales?: readonly string[];
  /** Which locales may be returned. Defaults to the released ones; tests pass their own. */
  available?: readonly Locale[];
};

/**
 * The language part of a tag, lower-cased: `it` for `it-IT`, `en` for `en-Latn-US`.
 *
 * `Intl.Locale` is the reliable reader, but it throws on a malformed tag — which a device can hand
 * back — so a plain split stands in for it then. `_` is accepted as well as `-`, since Android
 * reports `it_IT` in places.
 */
const languageOf = (tag: string): string | null => {
  try {
    return new Intl.Locale(tag.replace('_', '-')).language.toLowerCase();
  } catch {
    const [language] = tag.split(/[-_]/);

    return language ? language.toLowerCase() : null;
  }
};

/**
 * Which language the app opens in, decided before anything has been fetched.
 *
 * A stored choice wins, then the first of the device's preferred languages that is available, then
 * English. Device languages are matched on the language alone: `it-CH` is Italian, and there is no
 * regional variant of any locale to tell apart.
 *
 * The user's `language_id` from the settings resource is not an input here. It arrives later, over
 * the network, and the app applies it when it does; what this decides is the first frame, which
 * cannot wait for it.
 */
export const resolveLocale = ({
  stored,
  deviceLocales = [],
  available = RELEASED_LOCALES,
}: ResolveLocaleInput = {}): Locale => {
  const isAvailable = (value: string | null | undefined): value is Locale =>
    typeof value === 'string' && (available as readonly string[]).includes(value);

  if (isAvailable(stored)) {
    return stored;
  }

  for (const tag of deviceLocales) {
    const language = languageOf(tag);

    if (isAvailable(language)) {
      return language;
    }
  }

  return DEFAULT_LOCALE;
};

/**
 * The first of the device's languages there are translations for, released or not — what
 * onboarding stores as the user's `language`. `null` for a device in none of them, which sends no
 * preference rather than recording English as a choice the user never made.
 *
 * Not limited to `RELEASED_LOCALES` on purpose: a user who signs up while their language is held
 * back should get it the day it ships, without having to find the setting.
 */
export const deviceLanguage = (deviceLocales: readonly string[]): Locale | null => {
  for (const tag of deviceLocales) {
    const language = languageOf(tag);
    const locale = SUPPORTED_LOCALES.find((supported) => supported === language);

    if (locale) {
      return locale;
    }
  }

  return null;
};
